import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/w2lq6860_Logo%20Title%20-%20no%20background.png";
const BOOKING_URL = "https://alchemy-practice.clientsecure.me/";

// Pages where the header CTA switches from clinical "Book a Consultation"
// to tarot "Book a Reading". On these pages the button navigates directly
// to /book-a-reading (iter_16: was an in-page anchor scroll in iter_13).
const READING_CTA_PATHS = new Set([
    "/reflection-deck",
    "/alchemy-extended/alchemy-readings",
]);
const READING_CTA_TARGET = "/book-a-reading";

const approachItems = [
    { label: "My Approach", href: "/approach" },
    { label: "EMDR Therapy", href: "/emdr" },
    { label: "EMDR Intensive", href: "/emdr-intensive" },
    { label: "Brainspotting", href: "/brainspotting" },
    { label: "ACT Therapy", href: "/act" },
    { label: "DBT Therapy", href: "/dbt" },
];

// The "Other Services" dropdown items are intentionally kept in the codebase
// (commented out) but not rendered in the nav — per iter_17 user request the
// esoteric side of the practice is hidden from public navigation while all
// underlying routes and pages (/alchemy-extended, /alchemy-extended/alchemy-readings,
// /inner-compass, /alchemists-table, /book-a-reading) remain fully functional
// via direct URL. To reactivate, restore the extendedItems array below and
// re-add the <NavDropdown label="Other Services" ... /> block in the desktop
// and mobile menus.
// const extendedItems = [
//     { label: "Extended Path (Other Services)", href: "/alchemy-extended" },
//     { label: "The Reflection Deck (Tarot)", href: "/alchemy-extended/alchemy-readings" },
//     { label: "Inner Compass", href: "/inner-compass" },
//     { label: "Alchemist's Table", href: "/alchemists-table" },
// ];

const tailLinks = [
    { label: "Resources", href: "/resources", testid: "resources" },
    { label: "Blog", href: "/alchemy-insights", testid: "alchemy-insights" },
    { label: "About", href: "/about", testid: "about" },
    { label: "Contact", href: "/contact", testid: "contact" },
];

function NavDropdown({ label, href, items, testidKey }) {
    const [open, setOpen] = useState(false);
    let timer;
    const enter = () => { clearTimeout(timer); setOpen(true); };
    const leave = () => { timer = setTimeout(() => setOpen(false), 120); };

    return (
        <div
            className="relative"
            onMouseEnter={enter}
            onMouseLeave={leave}
            data-testid={`nav-${testidKey}-dropdown`}
        >
            <a
                href={href}
                className="gold-link whitespace-nowrap inline-flex items-center gap-1.5 text-[12px] tracking-[0.24em] uppercase font-medium text-charcoal/80 hover:text-charcoal"
                aria-haspopup="true"
                aria-expanded={open}
                data-testid={`nav-${testidKey}-trigger`}
            >
                {label}
                <ChevronDown size={12} strokeWidth={1.5} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </a>

            <div
                className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-all duration-300 ${
                    open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
                }`}
            >
                <div
                    className="min-w-[260px] py-3"
                    style={{ background: "#FAFAF8", border: "1px solid #E2DDD8" }}
                    data-testid={`nav-${testidKey}-menu`}
                >
                    {items.map((item) => (
                        <a
                            key={item.href}
                            href={item.href}
                            data-testid={`nav-${testidKey}-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                            className="group relative flex items-center px-6 py-3 transition-colors duration-300"
                            style={{
                                fontFamily: "Figtree",
                                fontSize: "11px",
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                color: "#8A7968",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = "#2C2825")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "#8A7968")}
                        >
                            <span
                                className="absolute left-0 top-1/2 -translate-y-1/2 transition-all duration-300 group-hover:w-3"
                                style={{ width: 0, height: 2, background: "#9B7D5A" }}
                            />
                            {item.label}
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const isReadingContext = READING_CTA_PATHS.has(location.pathname);
    const ctaLabel = isReadingContext ? "Book a Reading" : "Book a Consultation";
    const ctaHref = isReadingContext ? READING_CTA_TARGET : BOOKING_URL;
    const ctaProps = isReadingContext
        ? {} // iter_16: reading-context CTA now navigates directly to /book-a-reading
        : { target: "_blank", rel: "noopener noreferrer" };

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled ? "backdrop-blur-md py-2" : "py-4"
            }`}
            style={
                scrolled
                    ? { background: "rgba(250,250,248,0.92)", borderBottom: "1px solid #E2DDD8" }
                    : { background: "rgba(250,250,248,0.55)" }
            }
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12 flex items-center justify-between">
                <a href="/" data-testid="nav-logo" className="flex items-center group">
                    <div className="overflow-hidden h-16 sm:h-20 lg:h-[88px] w-[280px] sm:w-[360px] lg:w-[300px] xl:w-[360px] 2xl:w-[440px]">
                        <img
                            src={LOGO}
                            alt="Alchemy Practice"
                            className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
                        />
                    </div>
                </a>

                <nav className="hidden lg:flex items-center gap-6 xl:gap-8 2xl:gap-10">
                    <NavDropdown
                        label="Specialties & Approach"
                        href="/approach"
                        items={approachItems}
                        testidKey="approach"
                    />
                    {/* "Other Services" dropdown hidden in iter_17 — pages remain live via direct URL. */}
                    {tailLinks.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.testid}`}
                            className="gold-link text-[12px] tracking-[0.24em] uppercase font-medium text-charcoal/80 hover:text-charcoal"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={ctaHref}
                        {...ctaProps}
                        data-testid="nav-book-button"
                        className="hidden sm:inline-flex nav-book-button"
                        style={{ marginLeft: "28px" }}
                    >
                        {ctaLabel}
                    </a>
                    <button
                        onClick={() => setOpen(!open)}
                        aria-label="Toggle menu"
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden p-2 text-charcoal"
                    >
                        {open ? <X strokeWidth={1.25} /> : <Menu strokeWidth={1.25} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div className="lg:hidden px-6 py-6" data-testid="nav-mobile-menu" style={{ background: "#FAFAF8", borderTop: "1px solid #E2DDD8" }}>
                    <div className="flex flex-col gap-5">
                        <p className="editorial-eyebrow">Specialties & Approach</p>
                        {approachItems.map((item) => (
                            <a
                                key={item.href}
                                href={item.href}
                                onClick={() => setOpen(false)}
                                className="text-[13px] tracking-[0.24em] uppercase text-charcoal pl-4"
                                data-testid={`mobile-nav-approach-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                {item.label}
                            </a>
                        ))}
                        <div className="h-px w-full my-2" style={{ background: "#E2DDD8" }} />
                        {/* "Other Services" mobile block hidden in iter_17 — pages remain live via direct URL. */}
                        {tailLinks.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="text-[13px] tracking-[0.24em] uppercase text-charcoal"
                                data-testid={`mobile-nav-link-${l.testid}`}
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href={ctaHref}
                            {...(isReadingContext
                                ? { onClick: () => setOpen(false) }
                                : { target: "_blank", rel: "noopener noreferrer", onClick: () => setOpen(false) })}
                            className="nav-book-button mt-2"
                            data-testid="mobile-nav-book-button"
                        >
                            {ctaLabel}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
